export class VuePageViewportIntegrationError extends Error {
    code = 'WF7303';
    remediation;
    constructor(message, remediation) {
        super(`WF7303 ${message}`);
        this.name = 'VuePageViewportIntegrationError';
        this.remediation = remediation;
    }
}
function distance(value, fallback, name) {
    const resolved = value ?? fallback;
    if (!Number.isFinite(resolved) || resolved < 0) {
        throw new VuePageViewportIntegrationError(`${name} must be a finite non-negative number`, `Set ${name} to a browser pixel distance at or above zero.`);
    }
    return resolved;
}
export function installWefatPageViewportLifecycles(getPage, options = {}) {
    if (typeof getPage !== 'function') {
        throw new VuePageViewportIntegrationError('Page viewport integration requires an active-page resolver', 'Pass a function that returns the currently visible WebPageInstance or null.');
    }
    const staticReachBottomDistance = distance(options.reachBottomDistance, 50, 'reachBottomDistance');
    const getReachBottomDistance = options.getReachBottomDistance
        ?? (() => staticReachBottomDistance);
    if (typeof getReachBottomDistance !== 'function') {
        throw new VuePageViewportIntegrationError('getReachBottomDistance must be a function when provided', 'Pass a page-aware non-negative browser pixel distance resolver.');
    }
    const pullDistance = distance(options.pullDistance, 80, 'pullDistance');
    const isPullDownRefreshEnabled = options.isPullDownRefreshEnabled ?? (() => true);
    if (typeof isPullDownRefreshEnabled !== 'function') {
        throw new VuePageViewportIntegrationError('isPullDownRefreshEnabled must be a function when provided', 'Pass a page-aware boolean resolver for the generated pull-down refresh behavior.');
    }
    const browserWindow = options.window ?? (typeof window === 'undefined' ? null : window);
    const browserDocument = options.document ?? (typeof document === 'undefined' ? null : document);
    if (browserWindow === null || browserDocument === null) {
        throw new VuePageViewportIntegrationError('Page viewport integration requires a browser window and document', 'Install viewport lifecycles after the generated application enters a browser document.');
    }
    const viewportWindow = browserWindow;
    const viewportDocument = browserDocument;
    let reachedPage = null;
    let pullStart = null;
    let pullTriggered = false;
    let refreshingPage = null;
    function stopPullDownRefresh() {
        refreshingPage = null;
        viewportDocument.documentElement.removeAttribute('data-wefat-pull-down-refresh');
    }
    function pageScrollTo(target) {
        if (target === null || typeof target !== 'object') {
            throw new VuePageViewportIntegrationError('Page scrolling requires a normalized target object', 'Call the viewport through the generated wx.pageScrollTo adapter.');
        }
        const duration = distance(target.duration, 300, 'duration');
        let top;
        if (target.kind === 'scrollTop') {
            top = distance(target.scrollTop, 0, 'scrollTop');
        }
        else if (target.kind === 'selector') {
            if (typeof target.selector !== 'string' || target.selector.length === 0) {
                throw new VuePageViewportIntegrationError('Page scroll selector must be a non-empty string', 'Target an element inside the active generated page.');
            }
            if (!Number.isFinite(target.offsetTop)) {
                throw new VuePageViewportIntegrationError('Page scroll offsetTop must be finite', 'Set offsetTop to a finite browser pixel distance.');
            }
            const pageRoot = viewportDocument.querySelector('[data-wefat-page]');
            if (pageRoot === null) {
                throw new VuePageViewportIntegrationError('Page scroll requires an active generated page root', 'Call wx.pageScrollTo after the target page has mounted.');
            }
            let element;
            try {
                element = pageRoot.querySelector(target.selector);
            }
            catch {
                throw new VuePageViewportIntegrationError(`Page scroll selector is invalid: ${target.selector}`, 'Use a browser-compatible ID, class, child, descendant, or selector-list expression.');
            }
            if (element === null) {
                throw new VuePageViewportIntegrationError(`Page scroll selector did not match: ${target.selector}`, 'Wait for the target element to render or provide a valid scrollTop position.');
            }
            top = scrollTop() + element.getBoundingClientRect().top + target.offsetTop;
        }
        else {
            throw new VuePageViewportIntegrationError('Page scroll target kind is unsupported', 'Use the generated selector or scrollTop page-scroll target.');
        }
        try {
            viewportWindow.scrollTo({
                behavior: duration === 0 ? 'auto' : 'smooth',
                top: Math.max(0, top),
            });
        }
        catch {
            throw new VuePageViewportIntegrationError('Browser page scrolling failed', 'Run the generated application in a browser with Window.scrollTo support.');
        }
        if (duration > 0) {
            return new Promise((resolve) => {
                viewportWindow.setTimeout(resolve, duration);
            });
        }
    }
    function scrollTop() {
        return Math.max(0, viewportWindow.scrollY, viewportDocument.documentElement.scrollTop);
    }
    function onScroll() {
        const page = getPage();
        if (page === null) {
            reachedPage = null;
            return;
        }
        const currentScrollTop = scrollTop();
        page.pageScroll(currentScrollTop);
        const viewportBottom = currentScrollTop + viewportWindow.innerHeight;
        const scrollHeight = Math.max(viewportDocument.body.scrollHeight, viewportDocument.documentElement.scrollHeight);
        const reachBottomDistance = distance(getReachBottomDistance(), 50, 'reachBottomDistance');
        const atBottom = viewportBottom >= scrollHeight - reachBottomDistance;
        if (!atBottom) {
            if (reachedPage === page)
                reachedPage = null;
            return;
        }
        if (reachedPage !== page) {
            reachedPage = page;
            page.reachBottom();
        }
    }
    function onPointerdown(event) {
        pullStart = scrollTop() === 0 ? event.clientY : null;
        pullTriggered = false;
    }
    function onPointermove(event) {
        const page = getPage();
        if (page !== null
            && isPullDownRefreshEnabled()
            && pullStart !== null
            && !pullTriggered
            && refreshingPage === null
            && event.clientY - pullStart >= pullDistance) {
            pullTriggered = true;
            refreshingPage = page;
            viewportDocument.documentElement.setAttribute('data-wefat-pull-down-refresh', 'active');
            try {
                page.pullDownRefresh();
            }
            catch (error) {
                stopPullDownRefresh();
                throw error;
            }
        }
    }
    function resetPull() {
        pullStart = null;
        pullTriggered = false;
    }
    viewportWindow.addEventListener('scroll', onScroll, { passive: true });
    viewportWindow.addEventListener('pointerdown', onPointerdown, { passive: true });
    viewportWindow.addEventListener('pointermove', onPointermove, { passive: true });
    viewportWindow.addEventListener('pointerup', resetPull, { passive: true });
    viewportWindow.addEventListener('pointercancel', resetPull, { passive: true });
    return Object.freeze({
        pageScrollTo,
        stopPullDownRefresh,
        dispose() {
            stopPullDownRefresh();
            viewportWindow.removeEventListener('scroll', onScroll);
            viewportWindow.removeEventListener('pointerdown', onPointerdown);
            viewportWindow.removeEventListener('pointermove', onPointermove);
            viewportWindow.removeEventListener('pointerup', resetPull);
            viewportWindow.removeEventListener('pointercancel', resetPull);
        },
    });
}
//# sourceMappingURL=page-viewport.js.map